import { ArrowUpRight, Phone } from "lucide-react";
import { SELF_SERVICE, CLAIMS_LINES, AGENCY } from "@/data/content";
import { useQuote } from "@/context/QuoteContext";
import { Reveal } from "@/components/site/Motion";

export default function SelfService() {
  const { openQuote } = useQuote();

  const handle = (href) => {
    if (href === "#quote") openQuote("", "quote");
  };

  return (
    <section id="service" className="py-24 sm:py-32 bg-brand-ink text-brand-cream">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="max-w-2xl mb-14">
          <span className="overline text-brand-sand">Client service</span>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.02]">
            Self-service, <span className="italic text-brand-sand">the easy way.</span>
          </h2>
          <p className="mt-5 text-brand-cream/70 leading-relaxed">
            Already a client? Manage your policy without the wait. Pay a bill, grab your ID cards,
            request a change, or start a claim — anytime.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SELF_SERVICE.map((s, i) => {
            const Icon = s.icon;
            const Comp = s.href === "#quote" ? "button" : "a";
            const props = s.href === "#quote"
              ? { onClick: () => handle(s.href), type: "button" }
              : { href: s.href };
            return (
              <Reveal key={s.title} delay={i * 0.05}>
                <Comp {...props} data-testid={`service-${i}`}
                  className="group w-full text-left flex flex-col justify-between rounded-2xl border border-brand-cream/20 bg-brand-green/40 p-7 h-full hover:border-brand-sand hover:-translate-y-1 transition-all">
                  <div>
                    <Icon className="h-8 w-8 text-brand-sand" />
                    <h3 className="font-serif text-2xl mt-5">{s.title}</h3>
                    <p className="mt-2 text-brand-cream/65 text-sm">{s.desc}</p>
                  </div>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-brand-sand">
                    {s.cta} <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </span>
                </Comp>
              </Reveal>
            );
          })}
        </div>

        {/* Claims */}
        <div id="claims" className="mt-16 rounded-2xl border border-brand-cream/20 overflow-hidden grid lg:grid-cols-5">
          <div className="lg:col-span-2 bg-brand-clay p-9">
            <span className="overline text-white/70">Claims assistance</span>
            <h3 className="font-serif text-3xl sm:text-4xl mt-4 text-white">Need to file a claim?</h3>
            <p className="mt-4 text-white/85 text-sm leading-relaxed">
              Call us first — we'll guide you through every step. For 24/7 reporting, use your carrier's direct claims line below.
            </p>
            <a href={AGENCY.phoneHref} data-testid="claims-call-btn"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-brand-clay font-medium px-6 py-3 hover:bg-brand-cream transition-colors">
              <Phone className="h-4 w-4" /> Call {AGENCY.phone}
            </a>
          </div>
          <div className="lg:col-span-3 p-9 grid sm:grid-cols-2 gap-x-8 gap-y-5">
            {CLAIMS_LINES.map((c) => (
              <a key={c.carrier} href={`tel:${c.phone.replace(/[^0-9]/g, "")}`}
                data-testid={`claim-line-${c.carrier.toLowerCase().replace(/\s/g, "-")}`}
                className="flex items-center justify-between border-b border-brand-cream/15 pb-4 group">
                <div>
                  <div className="font-serif text-xl">{c.carrier}</div>
                  <div className="font-mono text-xs text-brand-cream/60">Claims line</div>
                </div>
                <span className="flex items-center gap-2 text-brand-sand text-sm group-hover:text-brand-cream transition-colors">
                  <Phone className="h-4 w-4" /> {c.phone}
                </span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
